import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Card, Row, Col, InputGroup, ListGroup, Badge, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import { useTranslation } from 'react-i18next';

interface Customer {
  cariID: number;
  eklenmeTarihi: string;
  takipNo: string;
  adiSoyadi: string;
  firmaAdi?: string;
  telefon1: string;
  telefon2?: string;
  ePosta?: string;
  adres?: string;
  tc?: string;
  cariVD?: string;
  cariVN?: string;
  turu: string;
}

interface Project {
  projeID: number;
  projeAdi: string;
  adres: string;
  projeDurumu: string;
  aktifMi: boolean;
}

interface Block {
  blokID: number;
  blokAdi: string;
  projeID: number;
  takipNo: string;
  aktifMi: boolean;
}

interface Apartment {
  daireID: number;
  daireNo: string;
  takipNo: string;
  sozlesmeTakipNo?: string;
  projeID: number;
  blokID: number;
  katID: number;
  cepheID: number;
  manzaraID: number;
  daireTipID: number;
  daireDurumu: string;
  yataySira: number;
  listeFiyati?: number;
  netM2?: number;
  brutM2?: number;
  paraBirimiID?: number;
  aciklama?: string;
  aktifMi: boolean;
  proje?: Project;
  blok?: Block;
  kat?: {
    katID: number;
    katAdi: string;
  };
  cephe?: {
    cepheID: number;
    cepheAdi: string;
  };
  manzara?: {
    manzaraID: number;
    manzaraAdi: string;
  };
  daireTipi?: {
    daireTipiID: number;
    tipiAdi: string;
  };
}

enum DaireDurumuEnum {
  Satista = 'Satışta',
  Rezerve = 'Rezerve',
  Sozlesme = 'Sözleşme',
  ArsaSahibi = 'ArsaSahibi',
  Taseron = 'Taşeron'
}

interface SalesContractDetails {
  cariID: number;
  daireID: number;
  sozlesmeTarihi: string;
  sozlesmeFiyati: number;
  sozlesmeDurumu: DaireDurumuEnum;
  rezerveBitisTarihi?: string;
  ekSartlar?: string;
}

const toDisplayDate = (iso: string): string => {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const fromDisplayDate = (display: string): string => {
  if (!display) return '';
  const parts = display.split('.');
  if (parts.length !== 3 || parts[2].length < 4) return '';
  return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
};

const SatisSozlesmesi: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Tarih gösterim state'leri (gg.aa.yyyy formatında)
  const [sozlesmeTarihiDisplay, setSozlesmeTarihiDisplay] = useState<string>(() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  });
  const [rezerveTarihiDisplay, setRezerveTarihiDisplay] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  });

  // Pre-selection ref: daire listesinden gelirken blok/daire yükleme sonrası restore etmek için
  const preSelectRef = useRef<{ blokID: number; daireID: number } | null>(null);

  // Müşteri yönetimi state'leri
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState<boolean>(false);
  const [loadingCustomers, setLoadingCustomers] = useState<boolean>(false);
  const [customerLoadedCount, setCustomerLoadedCount] = useState<number | null>(null);
  const [cariTipi, setCariTipi] = useState<'musteri' | 'cari'>('musteri');
  const [newCustomer, setNewCustomer] = useState<Omit<Customer, 'cariID' | 'eklenmeTarihi' | 'takipNo'>>({
    adiSoyadi: '',
    telefon1: '',
    telefon2: '',
    ePosta: '',
    adres: '',
    tc: '',
    firmaAdi: '',
    cariVD: '',
    cariVN: '',
    turu: 'Müşteri'
  });

  // Daire bilgileri state'leri
  const [projects, setProjects] = useState<Project[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(null);
  const [apartmentOzellikler, setApartmentOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  const [loadingBlocks, setLoadingBlocks] = useState<boolean>(false);
  const [loadingApartments, setLoadingApartments] = useState<boolean>(false);

  // Sözleşme bilgileri state'leri
  const [contractDetails, setContractDetails] = useState<SalesContractDetails>(() => {
    const today = new Date();
    const threeDaysLater = new Date();
    threeDaysLater.setDate(today.getDate() + 3);
    
    return {
      cariID: 0,
      daireID: 0,
      sozlesmeTarihi: today.toISOString().split('T')[0], // Bugünün tarihi
      sozlesmeFiyati: 0,
      sozlesmeDurumu: DaireDurumuEnum.Rezerve,
      rezerveBitisTarihi: threeDaysLater.toISOString().split('T')[0], // 3 gün sonrası
      ekSartlar: ''
    };
  });

  // Bir sonraki adıma geçme
  const goToNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Bir önceki adıma dönme
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Müşterileri yükle
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const tur = cariTipi === 'musteri' ? 'Müşteri' : 'Cari';
        const response = await axiosInstance.get(`/api/Cariler?tur=${encodeURIComponent(tur)}`);
        const customerData = Array.isArray(response.data) ? response.data : [];
        setCustomers(customerData);
        setCustomerLoadedCount(customerData.length > 0 ? customerData.length : null);
      } catch (error) {
        toast.error(t('msg.islemHatasi'));
        console.error('Müşteri yükleme hatası:', error);
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, [cariTipi]);

  // Daireler listesinden gelen seçili daire bilgisini kontrol et
  useEffect(() => {
    const selectedDaire = location.state?.selectedDaire;
    if (selectedDaire) {
      // Pre-selection bilgisini ref'e sakla; blok/daire yükleme sonrası restore edilecek
      preSelectRef.current = { blokID: selectedDaire.blokID, daireID: selectedDaire.daireID };

      // Adım 1'de kal — müşteri önce seçilecek
      setCurrentStep(1);

      // Sadece projeyi set et; blok/daire yükleme zinciri useEffect'ler ile tamamlanacak
      setSelectedProject({
        projeID: selectedDaire.projeID,
        projeAdi: selectedDaire.proje?.projeAdi || '',
        adres: '',
        projeDurumu: '',
        aktifMi: true
      });

      toast.info(t('msg.bilgi'));
    }

    // Müşteri detay sayfasından gelen seçili müşteri
    const preSelectedCustomer = location.state?.selectedCustomer;
    if (preSelectedCustomer) {
      setSelectedCustomer({
        cariID: preSelectedCustomer.cariID,
        eklenmeTarihi: preSelectedCustomer.eklenmeTarihi || '',
        takipNo: preSelectedCustomer.takipNo || '',
        adiSoyadi: preSelectedCustomer.adiSoyadi,
        firmaAdi: preSelectedCustomer.firmaAdi || undefined,
        telefon1: preSelectedCustomer.telefon1,
        telefon2: preSelectedCustomer.telefon2 || undefined,
        ePosta: preSelectedCustomer.ePosta || undefined,
        adres: preSelectedCustomer.adres || undefined,
        tc: preSelectedCustomer.tc || undefined,
        cariVD: preSelectedCustomer.cariVD || undefined,
        cariVN: preSelectedCustomer.cariVN || undefined,
        turu: preSelectedCustomer.turu || 'Bireysel'
      });
    }
  }, [location.state]);

  // Projeleri yükle
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await axiosInstance.get('/api/Projeler');
        const projectData = Array.isArray(response.data) ? response.data : [];
        // Sadece "Devam Ediyor" ve "Gelecek" durumundaki projeleri filtrele
        const activeProjects = projectData.filter((project: Project) => 
          project.projeDurumu === 'Devam Ediyor' || project.projeDurumu === 'Gelecek'
        );
        setProjects(activeProjects);
      } catch (error) {
        toast.error(t('msg.islemHatasi'));
        console.error('Proje yükleme hatası:', error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Proje seçildiğinde blokları yükle
  useEffect(() => {
    if (selectedProject) {
      const fetchBlocks = async () => {
        setLoadingBlocks(true);
        try {
          const response = await axiosInstance.get(`/api/Bloklar/proje/${selectedProject.projeID}`);
          const blockData = Array.isArray(response.data) ? response.data : [];
          setBlocks(blockData);

          if (preSelectRef.current) {
            // Daire listesinden gelindi: pre-select etmek istediğimiz bloğu bul
            const match = blockData.find((b: Block) => b.blokID === preSelectRef.current!.blokID);
            setSelectedBlock(match || null);
            if (!match) {
              preSelectRef.current = null; // bulunamadı, vazgeç
              setApartments([]);
              setSelectedApartment(null);
            }
            // match varsa apartments yükleme selectedBlock useEffect'i tetikleyecek
          } else {
            setSelectedBlock(null);
            setApartments([]);
            setSelectedApartment(null);
          }
        } catch (error) {
          toast.error(t('msg.islemHatasi'));
          console.error('Blok yükleme hatası:', error);
          setBlocks([]);
        } finally {
          setLoadingBlocks(false);
        }
      };

      fetchBlocks();
    } else {
      setBlocks([]);
      setSelectedBlock(null);
      setApartments([]);
      setSelectedApartment(null);
    }
  }, [selectedProject]);

  // Blok seçildiğinde daireleri yükle
  useEffect(() => {
    if (selectedBlock) {
      const fetchApartments = async () => {
        setLoadingApartments(true);
        try {
          const response = await axiosInstance.get(`/api/Daireler/blok/${selectedBlock.blokID}`);
          const apartmentData = Array.isArray(response.data) ? response.data : [];
          setApartments(apartmentData);

          if (preSelectRef.current) {
            // Daire listesinden gelindi: pre-select etmek istediğimiz daireyi bul
            const match = apartmentData.find((a: Apartment) => a.daireID === preSelectRef.current!.daireID);
            setSelectedApartment(match || null);
            preSelectRef.current = null; // zincir tamamlandı
          } else {
            setSelectedApartment(null);
          }
        } catch (error) {
          toast.error(t('msg.islemHatasi'));
          console.error('Daire yükleme hatası:', error);
          setApartments([]);
        } finally {
          setLoadingApartments(false);
        }
      };

      fetchApartments();
    } else {
      setApartments([]);
      setSelectedApartment(null);
    }
  }, [selectedBlock]);

  // Müşteri arama
  const filteredCustomers = customers.filter(customer => {
    if (!customer || typeof customer !== 'object') return false;
    
    const fullName = (customer.adiSoyadi || '').toLowerCase();
    const phone = customer.telefon1 ? customer.telefon1.toString().toLowerCase() : '';
    const email = customer.ePosta ? customer.ePosta.toLowerCase() : '';
    const tcNo = customer.tc ? customer.tc.toString().toLowerCase() : '';
    const searchTermLower = searchTerm.toLowerCase();
    
    if (!searchTerm) return true; // Arama terimi yoksa tüm müşterileri göster
    
    return (
      fullName.includes(searchTermLower) || 
      phone.includes(searchTermLower) || 
      email.includes(searchTermLower) ||
      tcNo.includes(searchTermLower)
    );
  });

  // Müşteri seçme
  const handleSelectCustomer = (customer: Customer) => {
    console.log('Seçilen müşteri:', customer);
    setSelectedCustomer(customer);
  };

  // Sözleşme detayları değişim handler'ı
  const handleContractDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setContractDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Tarih input handler — gg.aa.yyyy formatını otomatik biçimlendirir
  const handleDateDisplayChange = (
    name: 'sozlesmeTarihi' | 'rezerveBitisTarihi',
    value: string
  ) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) formatted = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4);
    else if (digits.length > 2) formatted = digits.slice(0, 2) + '.' + digits.slice(2);
    if (name === 'sozlesmeTarihi') setSozlesmeTarihiDisplay(formatted);
    else setRezerveTarihiDisplay(formatted);
    const iso = fromDisplayDate(formatted);
    if (iso) handleContractDetailsChange({ target: { name, value: iso } } as React.ChangeEvent<HTMLInputElement>);
  };

  // Sözleşme durumu değiştiğinde rezerve bitiş tarihini güncelle
  useEffect(() => {
    if (contractDetails.sozlesmeDurumu === DaireDurumuEnum.Rezerve) {
      // Rezerve seçildiğinde 3 gün sonrası olarak ayarla
      const threeDaysLater = new Date();
      threeDaysLater.setDate(threeDaysLater.getDate() + 3);
      const iso = threeDaysLater.toISOString().split('T')[0];
      setContractDetails(prev => ({
        ...prev,
        rezerveBitisTarihi: iso
      }));
      setRezerveTarihiDisplay(toDisplayDate(iso));
    } else {
      setContractDetails(prev => ({
        ...prev,
        rezerveBitisTarihi: ''
      }));
      setRezerveTarihiDisplay('');
    }
  }, [contractDetails.sozlesmeDurumu]);

  // Seçili müşteri ve daire değiştiğinde sözleşme detaylarını güncelle
  useEffect(() => {
    if (selectedCustomer && selectedApartment) {
      setContractDetails(prev => ({
        ...prev,
        cariID: selectedCustomer.cariID,
        daireID: selectedApartment.daireID,
        sozlesmeFiyati: selectedApartment.listeFiyati || 0
      }));
    }
  }, [selectedCustomer, selectedApartment]);

  useEffect(() => {
    if (selectedApartment?.takipNo) {
      axiosInstance.get(`/api/DaireOzellikler/daire/${selectedApartment.takipNo}`)
        .then(res => setApartmentOzellikler(res.data))
        .catch(() => setApartmentOzellikler([]));
    } else {
      setApartmentOzellikler([]);
    }
  }, [selectedApartment]);

  // Yeni müşteri formu değişikliklerini izleme
  const handleNewCustomerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
  };

  // Yeni müşteri kaydetme
  const handleSaveNewCustomer = async () => {
    try {
      const response = await axiosInstance.post('/api/Cariler', newCustomer);
      const savedCustomer = response.data;
      
      // Müşteri listesini güncelle
      setCustomers(prev => [...prev, savedCustomer]);
      
      // Yeni eklenen müşteriyi seç
      setSelectedCustomer(savedCustomer);
      
      // Modal'ı kapat ve formu temizle
      setShowNewCustomerModal(false);
      setNewCustomer({
        adiSoyadi: '',
        telefon1: '',
        telefon2: '',
        ePosta: '',
        adres: '',
        tc: '',
        firmaAdi: '',
        cariVD: '',
        cariVN: '',
        turu: 'Müşteri'
      });
      
      toast.success(t('msg.islemBasarili'));
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      console.error('Müşteri ekleme hatası:', error);
    }
  };

  // Ortak CSS
  const wizardCSS = `
    .ss-wrap { background: #f8fafc; min-height: 100vh; padding: 24px; font-family: 'Inter', 'Segoe UI', sans-serif; }
    /* ── Stepper ── */
    .ss-stepper { background: #fff; border-radius: 20px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 6px rgba(15,23,42,0.05); padding: 28px 40px; margin-bottom: 28px; display: flex; align-items: center; }
    .ss-step { display: flex; flex-direction: column; align-items: center; flex: 1; position: relative; z-index: 1; }
    .ss-step-circle { width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; margin-bottom: 8px; transition: all 0.3s; }
    .ss-step-circle.done { border: 2px solid #16a34a; background: #16a34a; color: #fff; box-shadow: 0 0 0 4px rgba(22,163,74,0.15); }
    .ss-step-circle.active { border: 2px solid #cd6906; background: #cd6906; color: #fff; box-shadow: 0 0 0 4px rgba(205,105,6,0.18); }
    .ss-step-circle.pending { border: 2px solid #d1d5db; background: #fff; color: #9ca3af; }
    .ss-step-label { font-size: 13px; font-weight: 600; letter-spacing: 0.3px; text-align: center; margin-top: 6px; }
    .ss-step-label.done { color: #16a34a; } .ss-step-label.active { color: #cd6906; } .ss-step-label.pending { color: #94a3b8; }
    .ss-track { flex: 1; height: 3px; border-radius: 2px; margin-bottom: 28px; transition: background 0.3s; }
    .ss-track.done { background: #16a34a; } .ss-track.pending { background: #e2e8f0; }
    /* ── Cards ── */
    .ss-card { background: #fff; border-radius: 20px; border: 1.5px solid #f1f5f9; box-shadow: 0 1px 6px rgba(15,23,42,0.05); margin-bottom: 20px; overflow: hidden; }
    .ss-card-header { padding: 18px 24px; border-bottom: 1.5px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
    .ss-card-title { font-size: 14px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 9px; }
    .ss-card-icon { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 15px; }
    .ss-card-body { padding: 24px; }
    /* ── Search ── */
    .ss-search-wrap { position: relative; margin-bottom: 16px; }
    .ss-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; pointer-events: none; }
    .ss-search-input { width: 100%; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 11px 14px 11px 40px; font-size: 14px; color: #0f172a; outline: none; transition: all 0.15s; font-family: inherit; box-sizing: border-box; }
    .ss-search-input:focus { border-color: #cd6906; box-shadow: 0 0 0 3px rgba(205,105,6,0.1); background: #fff; }
    .ss-search-input::placeholder { color: #94a3b8; }
    /* ── Customer List ── */
    .ss-clist { max-height: 280px; overflow-y: auto; border-radius: 12px; border: 1.5px solid #f1f5f9; }
    .ss-citem { padding: 13px 16px; display: flex; align-items: center; justify-content: space-between; cursor: pointer; border-bottom: 1.5px solid #f8fafc; border-left: 3px solid transparent; transition: all 0.12s; }
    .ss-citem:last-child { border-bottom: none; }
    .ss-citem:hover { background: #fef9f4; border-left: 3px solid #cd6906; }
    .ss-citem.selected { background: #fff7ed; border: 1px solid #cd6906; border-radius: 8px; }
    .ss-citem-name { font-size: 14px; font-weight: 600; color: #0f172a; }
    .ss-citem-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
    .ss-citem-check { width: 22px; height: 22px; border-radius: 50%; background: #cd6906; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px; flex-shrink: 0; }
    /* ── Selected Customer Card ── */
    .ss-selected-cust { border-radius: 16px; border: 2px solid #16a34a; background: #f0fdf4; padding: 18px 22px; display: flex; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
    .ss-selected-cust-avatar { width: 46px; height: 46px; border-radius: 14px; background: #16a34a; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #fff; flex-shrink: 0; }
    .ss-selected-cust-name { font-size: 15px; font-weight: 700; color: #14532d; }
    .ss-selected-cust-sub { font-size: 12px; color: #166534; margin-top: 3px; }
    .ss-verified { display: inline-flex; align-items: center; gap: 5px; background: #dcfce7; color: #15803d; font-size: 11px; font-weight: 600; border-radius: 20px; padding: 3px 10px; margin-top: 6px; }
    /* ── Dropdowns ── */
    .ss-field-wrap { display: flex; flex-direction: column; }
    .ss-label { font-size: 11px; font-weight: 600; color: #64748b; letter-spacing: 0.5px; margin-bottom: 6px; }
    .ss-select { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 11px 36px 11px 14px; font-size: 14px; color: #0f172a; outline: none; cursor: pointer; transition: all 0.15s; font-family: inherit; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; width: 100%; }
    .ss-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background-color: #fff; }
    .ss-select:disabled { opacity: 0.5; cursor: not-allowed; }
    .ss-input { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 11px 14px; font-size: 14px; color: #0f172a; outline: none; transition: all 0.15s; font-family: inherit; width: 100%; box-sizing: border-box; }
    .ss-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background: #fff; }
    .ss-input::placeholder { color: #94a3b8; }
    .ss-input-price-wrap { display: flex; align-items: center; border: 1.5px solid #e2e8f0; border-radius: 12px; background: #f8fafc; overflow: hidden; transition: all 0.15s; }
    .ss-input-price-wrap:focus-within { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background: #fff; }
    .ss-input-price { background: none; border: none; padding: 11px 14px; font-size: 15px; font-weight: 700; color: #0f172a; outline: none; flex: 1; font-family: inherit; }
    .ss-input-currency { background: #f1f5f9; padding: 11px 14px; font-size: 14px; font-weight: 600; color: #64748b; border-left: 1.5px solid #e2e8f0; }
    .ss-textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; font-size: 14px; color: #0f172a; outline: none; transition: all 0.15s; font-family: inherit; width: 100%; box-sizing: border-box; resize: vertical; min-height: 110px; }
    .ss-textarea:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.08); background: #fff; }
    /* ── Daire Info Grid ── */
    .ss-info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
    .ss-info-item { background: #f8fafc; border-radius: 12px; padding: 14px 16px; border: 1.5px solid #f1f5f9; }
    .ss-info-item-label { font-size: 11px; font-weight: 600; color: #94a3b8; letter-spacing: 0.5px; margin-bottom: 6px; display: flex; align-items: center; gap: 5px; }
    .ss-info-item-val { font-size: 14px; font-weight: 600; color: #0f172a; }
    .ss-info-item-val.price { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.5px; }
    /* ── Summary cards ── */
    .ss-summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
    .ss-summary-card { border-radius: 16px; border: 1.5px solid #f1f5f9; background: #f8fafc; padding: 20px; }
    .ss-summary-card-header { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
    .ss-summary-card-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
    .ss-summary-card-title { font-size: 13px; font-weight: 700; color: #0f172a; }
    .ss-summary-field { margin-bottom: 8px; display: flex; flex-direction: column; }
    .ss-summary-label { font-size: 11px; color: #94a3b8; font-weight: 500; }
    .ss-summary-val { font-size: 13px; font-weight: 600; color: #334155; margin-top: 1px; }
    /* ── Form grid ── */
    .ss-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 18px; }
    .ss-form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 18px; margin-bottom: 18px; }
    /* ── Status pills ── */
    .ss-status-opts { display: flex; gap: 10px; flex-wrap: wrap; }
    .ss-status-pill { padding: 9px 18px; border-radius: 50px; border: 1.5px solid #e2e8f0; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; background: #fff; color: #64748b; font-family: inherit; }
    .ss-status-pill:hover { border-color: #94a3b8; }
    .ss-status-pill.active-rezerve { border-color: #f59e0b; background: #fef3c7; color: #b45309; }
    .ss-status-pill.active-sozlesme { border-color: #3b82f6; background: #dbeafe; color: #1d4ed8; }
    .ss-status-pill.active-arsa { border-color: #8b5cf6; background: #ede9fe; color: #6d28d9; }
    .ss-status-pill.active-taseron { border-color: #0ea5e9; background: #e0f2fe; color: #0369a1; }
    /* ── Buttons ── */
    .ss-btn-row { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
    .ss-btn-back { background: transparent; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 20px; font-size: 14px; font-weight: 600; color: #6b7280; cursor: pointer; font-family: inherit; transition: all 0.15s; display: flex; align-items: center; gap: 8px; }
    .ss-btn-back:hover { border-color: #cd6906; color: #cd6906; }
    .ss-btn-next { background: #cd6906; border: none; border-radius: 8px; padding: 12px 28px; font-size: 15px; font-weight: 700; color: #fff; cursor: pointer; font-family: inherit; transition: all 0.15s; display: flex; align-items: center; gap: 8px; }
    .ss-btn-next:hover { background: #b85d05; box-shadow: 0 4px 12px rgba(205,105,6,0.3); } .ss-btn-next:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
    .ss-btn-save { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); border: none; border-radius: 12px; padding: 11px 32px; font-size: 14px; font-weight: 700; color: #fff; cursor: pointer; font-family: inherit; transition: all 0.15s; display: flex; align-items: center; gap: 8px; box-shadow: 0 2px 10px rgba(22,163,74,0.25); }
    .ss-btn-save:hover { box-shadow: 0 4px 16px rgba(22,163,74,0.35); } .ss-btn-save:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
    .ss-btn-new-cust { background: #cd6906; border: none; border-radius: 10px; padding: 9px 18px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; display: flex; align-items: center; gap: 7px; }
    .ss-btn-new-cust:hover { background: #b85d05; }
    /* ── Modal ── */
    .ss-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 24px; backdrop-filter: blur(3px); }
    .ss-modal { background: #fff; border-radius: 20px; width: 100%; max-width: 520px; box-shadow: 0 20px 60px rgba(15,23,42,0.18); overflow: hidden; }
    .ss-modal-header { padding: 22px 28px; border-bottom: 1.5px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; }
    .ss-modal-title { font-size: 16px; font-weight: 700; color: #0f172a; }
    .ss-modal-close { width: 32px; height: 32px; border-radius: 8px; background: #f8fafc; border: 1.5px solid #f1f5f9; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; color: #64748b; transition: all 0.15s; }
    .ss-modal-close:hover { background: #f1f5f9; }
    .ss-modal-body { padding: 24px 28px; }
    .ss-modal-footer { padding: 18px 28px; border-top: 1.5px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 10px; }
    .ss-btn-cancel { background: none; border: 1.5px solid #e2e8f0; border-radius: 11px; padding: 9px 20px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; font-family: inherit; transition: all 0.15s; }
    .ss-btn-cancel:hover { border-color: #94a3b8; }
    .ss-btn-save-sm { background: #16a34a; border: none; border-radius: 11px; padding: 9px 22px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; font-family: inherit; transition: background 0.15s; }
    .ss-btn-save-sm:hover { background: #15803d; }
    .ss-load-notice { display: flex; align-items: center; gap: 10px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 16px; color: #374151; font-size: 13px; font-weight: 500; margin-bottom: 14px; }
    .ss-empty { text-align: center; padding: 40px 24px; color: #94a3b8; font-size: 13px; }
    .ss-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.3; }
    .ss-divider { display: flex; align-items: center; gap: 10px; margin: 18px 0; }
    .ss-divider-line { flex: 1; height: 1px; background: #f1f5f9; }
    .ss-divider-text { font-size: 11px; color: #94a3b8; font-weight: 500; }
  `;

  // Adım 1 - Müşteri Bilgileri
  const renderCustomerInfo = () => {
    return (
      <>
        {/* Seçili Müşteri Highlight */}
        {selectedCustomer && (
          <div className="ss-selected-cust">
            <div className="ss-selected-cust-avatar"><i className="fas fa-user" /></div>
            <div style={{ flex: 1 }}>
              <div className="ss-selected-cust-name">{selectedCustomer.adiSoyadi}</div>
              <div className="ss-selected-cust-sub">
                {selectedCustomer.telefon1 && <span><i className="fas fa-phone" style={{ marginRight: 4 }} />{selectedCustomer.telefon1}</span>}
                {selectedCustomer.telefon1 && selectedCustomer.ePosta && <span style={{ margin: '0 8px', color: '#bbf7d0' }}>|</span>}
                {selectedCustomer.ePosta && <span><i className="fas fa-envelope" style={{ marginRight: 4 }} />{selectedCustomer.ePosta}</span>}
              </div>
              {selectedCustomer.tc && <div className="ss-selected-cust-sub" style={{ marginTop: 2 }}>TC: {selectedCustomer.tc}</div>}
              <div className="ss-verified"><i className="fas fa-check" style={{ marginRight: 4 }} />Müşteri Seçildi</div>
            </div>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: 13, fontWeight: 600 }}
              onClick={() => setSelectedCustomer(null)}>{t('btn.degistir')}</button>
          </div>
        )}

        {/* Müşteri Arama ve Seçim */}
        <div className="ss-card">
          <div className="ss-card-header">
            <div className="ss-card-title">
              <div className="ss-card-icon" style={{ background: '#eff6ff' }}><i className="fas fa-search" style={{ color: '#3b82f6' }} /></div>
              Müşteri Ara & Seç
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 10, padding: 3, gap: 3 }}>
                <button
                  style={{ border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', background: cariTipi === 'musteri' ? '#cd6906' : 'transparent', color: cariTipi === 'musteri' ? '#fff' : '#6b7280' }}
                  onClick={() => { setCariTipi('musteri'); setSelectedCustomer(null); setSearchTerm(''); }}
                >{t('label.musteri')}</button>
                <button
                  style={{ border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', background: cariTipi === 'cari' ? '#cd6906' : 'transparent', color: cariTipi === 'cari' ? '#fff' : '#6b7280' }}
                  onClick={() => { setCariTipi('cari'); setSelectedCustomer(null); setSearchTerm(''); }}
                >{t('cari.tedarikci')}</button>
              </div>
              <button className="ss-btn-new-cust" onClick={() => setShowNewCustomerModal(true)}>
                <i className="fas fa-plus" /> Yeni Müşteri
              </button>
            </div>
          </div>
          <div className="ss-card-body">
            {customerLoadedCount !== null && (
              <div className="ss-load-notice">
                <i className="fas fa-check-circle" style={{ color: '#16a34a', fontSize: 15 }} />
                {customerLoadedCount} müşteri yüklendi
              </div>
            )}
            <div className="ss-search-wrap">
              <i className="fas fa-search ss-search-icon"></i>
              <input
                type="text"
                className="ss-search-input"
                placeholder={t('label.ara')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            {loadingCustomers ? (
              <div className="ss-empty"><div className="ss-empty-icon"><i className="fas fa-spinner fa-spin" /></div>Müşteriler yükleniyor...</div>
            ) : customers.length === 0 ? (
              <div className="ss-empty"><div className="ss-empty-icon"><i className="fas fa-users" /></div>Henüz kayıtlı müşteri yok.</div>
            ) : (
              <div className="ss-clist">
                {filteredCustomers.length === 0 ? (
                  <div className="ss-empty"><div className="ss-empty-icon"><i className="fas fa-search" /></div>{t('msg.veriYok')}</div>
                ) : filteredCustomers.map((customer, index) => (
                  <div
                    key={`c-${customer.cariID}-${index}`}
                    className={`ss-citem${selectedCustomer?.cariID === customer.cariID ? ' selected' : ''}`}
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <div>
                      <div className="ss-citem-name">
                        {customer.adiSoyadi}
                        {customer.firmaAdi && <span style={{ fontWeight: 500, color: '#64748b' }}> - {customer.firmaAdi}</span>}
                        {customer.telefon1 && <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 10, fontSize: 12.5 }}><i className="fas fa-phone" style={{ marginRight: 3 }} />{customer.telefon1}</span>}
                      </div>
                    </div>
                    {selectedCustomer?.cariID === customer.cariID && (
                      <div className="ss-citem-check"><i className="fas fa-check" /></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="ss-btn-row">
          <span />
          <button className="ss-btn-next" onClick={goToNextStep} disabled={!selectedCustomer}>
            İleri
            <i className="fas fa-chevron-right" style={{ fontSize: 11 }} />
          </button>
        </div>

        {/* Yeni Müşteri Modal */}
        {showNewCustomerModal && (
          <div className="ss-modal-overlay" onClick={() => setShowNewCustomerModal(false)}>
            <div className="ss-modal" onClick={e => e.stopPropagation()}>
              <div className="ss-modal-header">
                <div className="ss-modal-title"><i className="fas fa-user-plus" style={{ marginRight: 8, color: '#cd6906' }} />Yeni Müşteri Ekle</div>
                <button className="ss-modal-close" onClick={() => setShowNewCustomerModal(false)}><i className="fas fa-times" /></button>
              </div>
              <div className="ss-modal-body">
                <div style={{ marginBottom: 16 }}>
                  <div className="ss-label">Ad Soyad *</div>
                  <input className="ss-input" type="text" name="adiSoyadi" value={newCustomer.adiSoyadi}
                    onChange={handleNewCustomerChange} placeholder={t('label.ara')} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div>
                    <div className="ss-label">TC Kimlik No</div>
                    <input className="ss-input" type="text" name="tc" value={newCustomer.tc}
                      onChange={handleNewCustomerChange} placeholder="TC Kimlik No" />
                  </div>
                  <div>
                    <div className="ss-label">Telefon</div>
                    <input className="ss-input" type="text" name="telefon1" value={newCustomer.telefon1}
                      onChange={handleNewCustomerChange} placeholder={t('label.ara')} />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div className="ss-label">E-posta</div>
                  <input className="ss-input" type="email" name="ePosta" value={newCustomer.ePosta}
                    onChange={handleNewCustomerChange} placeholder="E-posta adresi" />
                </div>
                <div>
                  <div className="ss-label">Adres</div>
                  <textarea className="ss-textarea" name="adres" value={newCustomer.adres}
                    onChange={handleNewCustomerChange} placeholder={t('label.ara')} rows={3} />
                </div>
              </div>
              <div className="ss-modal-footer">
                <button className="ss-btn-cancel" onClick={() => setShowNewCustomerModal(false)}>{t('btn.iptal')}</button>
                <button className="ss-btn-save-sm" onClick={handleSaveNewCustomer} disabled={!newCustomer.adiSoyadi}>Kaydet ve Seç</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Adım 2 - Daire Bilgileri
  const renderApartmentInfo = () => {
    return (
      <>
        <div className="ss-card">
          <div className="ss-card-header">
            <div className="ss-card-title">
              <div className="ss-card-icon" style={{ background: '#fef9c3' }}>
                <i className="fas fa-home" style={{ color: '#b45309' }} />
              </div>
              Daire Seçimi
            </div>
          </div>
          <div className="ss-card-body">
            <div className="ss-form-grid-3" style={{ marginBottom: 20 }}>
              <div className="ss-field-wrap">
                <div className="ss-label">Proje *</div>
                <select
                  className="ss-select"
                  value={selectedProject?.projeID || ''}
                  onChange={(e) => {
                    const projectId = parseInt(e.target.value);
                    const project = projects.find(p => p.projeID === projectId) || null;
                    setSelectedProject(project);
                  }}
                  disabled={loadingProjects}
                >
                  <option value="">{loadingProjects ? t('msg.yukleniyor') : 'Proje seçiniz...'}
                  </option>
                  {projects.map(project => (
                    <option key={project.projeID} value={project.projeID}>
                      {project.projeAdi} ({project.projeDurumu})
                    </option>
                  ))}
                </select>
              </div>
              <div className="ss-field-wrap">
                <div className="ss-label">Blok *</div>
                <select
                  className="ss-select"
                  value={selectedBlock?.blokID || ''}
                  onChange={(e) => {
                    const blockId = parseInt(e.target.value);
                    const block = blocks.find(b => b.blokID === blockId) || null;
                    setSelectedBlock(block);
                  }}
                  disabled={!selectedProject || loadingBlocks}
                >
                  <option value="">{!selectedProject ? 'Önce proje seçiniz...' : loadingBlocks ? t('msg.yukleniyor') : 'Blok seçiniz...'}</option>
                  {blocks.map(block => (
                    <option key={block.blokID} value={block.blokID}>{block.blokAdi}</option>
                  ))}
                </select>
              </div>
              <div className="ss-field-wrap">
                <div className="ss-label">Daire *</div>
                <select
                  className="ss-select"
                  value={selectedApartment?.daireID || ''}
                  onChange={(e) => {
                    const apartmentId = parseInt(e.target.value);
                    const apartment = apartments.find(a => a.daireID === apartmentId) || null;
                    setSelectedApartment(apartment);
                  }}
                  disabled={!selectedBlock || loadingApartments}
                >
                  <option value="">{!selectedBlock ? 'Önce blok seçiniz...' : loadingApartments ? t('msg.yukleniyor') : 'Daire seçiniz...'}</option>
                  {apartments.map(apartment => (
                    <option key={apartment.daireID} value={apartment.daireID}>
                      Daire {apartment.daireNo} - {apartment.netM2 || apartment.brutM2 || 'N/A'} m²
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedApartment && (
              <>
              <div className="ss-info-grid" style={{ marginBottom: 8 }}>
                <div className="ss-info-item">
                  <div className="ss-info-item-label"><i className="fas fa-door-open" style={{ marginRight: 4 }} />Daire No</div>
                  <div className="ss-info-item-val">{selectedApartment.daireNo}</div>
                </div>
                <div className="ss-info-item">
                  <div className="ss-info-item-label"><i className="fas fa-building" style={{ marginRight: 4 }} />Blok</div>
                  <div className="ss-info-item-val">{selectedApartment.blok?.blokAdi || '—'}</div>
                </div>
                <div className="ss-info-item">
                  <div className="ss-info-item-label"><i className="fas fa-layer-group" style={{ marginRight: 4 }} />Kat</div>
                  <div className="ss-info-item-val">{selectedApartment.kat?.katAdi || '—'}</div>
                </div>
                <div className="ss-info-item">
                  <div className="ss-info-item-label"><i className="fas fa-compass" style={{ marginRight: 4 }} />Cephe</div>
                  <div className="ss-info-item-val">{selectedApartment.cephe?.cepheAdi || '—'}</div>
                </div>
                <div className="ss-info-item">
                  <div className="ss-info-item-label"><i className="fas fa-ruler" style={{ marginRight: 4 }} />Net m²</div>
                  <div className="ss-info-item-val">{selectedApartment.netM2 ? `${selectedApartment.netM2} m²` : '—'}</div>
                </div>
                <div className="ss-info-item">
                  <div className="ss-info-item-label"><i className="fas fa-tag" style={{ marginRight: 4 }} />Liste Fiyatı</div>
                  <div className="ss-info-item-val price">
                    {selectedApartment.listeFiyati
                      ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(selectedApartment.listeFiyati)
                      : '—'}
                  </div>
                </div>
              </div>
              {apartmentOzellikler.length > 0 && (
                <div className="ss-info-grid" style={{ marginTop: 0, marginBottom: 8 }}>
                  {apartmentOzellikler.map((oz, idx) => (
                    <div key={idx} className="ss-info-item">
                      <div className="ss-info-item-label"><i className="fas fa-tags" style={{ marginRight: 4 }} />{oz.ozellik}</div>
                      <div className="ss-info-item-val">{oz.deger}</div>
                    </div>
                  ))}
                </div>
              )}
              </>
            )}
          </div>
        </div>

        <div className="ss-btn-row">
          <button className="ss-btn-back" onClick={goToPreviousStep}>
            <i className="fas fa-chevron-left" style={{ fontSize: 11 }} />
            Geri
          </button>
          <button className="ss-btn-next" onClick={goToNextStep} disabled={!selectedApartment}>
            İleri
            <i className="fas fa-chevron-right" style={{ fontSize: 11 }} />
          </button>
        </div>
      </>
    );
  };

  // Sözleşme kaydetme fonksiyonu
  const handleSaveContract = async () => {
    try {
      // Validation kontrolü
      if (!selectedCustomer) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
      
      if (!selectedApartment) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
      
      if (!contractDetails.sozlesmeTarihi) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
      
      if (contractDetails.sozlesmeFiyati <= 0) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
      
      if (contractDetails.sozlesmeDurumu === DaireDurumuEnum.Rezerve && !contractDetails.rezerveBitisTarihi) {
        toast.error(t('msg.islemHatasi'));
        return;
      }

      // API için sözleşme verilerini hazırla
      const sozlesmeData = {
        DaireID: selectedApartment.daireID,
        CariID: selectedCustomer.cariID,
        SozlesmeTarihi: contractDetails.sozlesmeTarihi,
        RezerveBitisTarihi: contractDetails.sozlesmeDurumu === DaireDurumuEnum.Rezerve 
          ? contractDetails.rezerveBitisTarihi 
          : null,
        SozlesmeDurumu: contractDetails.sozlesmeDurumu,
        SozlesmeFiyati: contractDetails.sozlesmeFiyati,
        PlanlananOdeme: 0, // Kullanıcı istediği gibi 0 olarak set ediliyor
        OrtalamaVade: 0, // Kullanıcı istediği gibi 0 olarak set ediliyor
        OzelSartlar: contractDetails.ekSartlar || null
      };

      console.log('API\'ye gönderilen veri:', sozlesmeData);

      // API çağrısı
      const response = await axiosInstance.post('/api/Sozlesmeler', sozlesmeData);
      
      if (response.status === 201) {
        toast.success(response.data?.onayDurumu === 'Bekliyor'
          ? 'Sözleşme oluşturuldu ve proje yöneticisinin onayına gönderildi.'
          : t('msg.islemBasarili'));
        console.log('Oluşturulan sözleşme:', response.data);
        
        // Oluşturulan sözleşmenin detay sayfasına yönlendir
        const takipNo = response.data.sozlesmeTakipNo;
        if (takipNo) {
          navigate(`/sozlesme-detay/${takipNo}`);
        }
      }
      
    } catch (error: any) {
      console.error('Sözleşme kaydetme hatası:', error);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data  || t('msg.islemHatasi'));
      } else if (error.response?.status === 409) {
        toast.error(t('msg.islemHatasi'));
      } else {
        toast.error(t('msg.islemHatasi'));
      }
    }
  };

  // Adım 3 - Sözleşme Bilgileri
  const renderContractInfo = () => {
    const getStatusPillClass = (val: DaireDurumuEnum) => {
      if (contractDetails.sozlesmeDurumu !== val) return 'ss-status-pill';
      if (val === DaireDurumuEnum.Rezerve) return 'ss-status-pill active-rezerve';
      if (val === DaireDurumuEnum.Sozlesme) return 'ss-status-pill active-sozlesme';
      if (val === DaireDurumuEnum.ArsaSahibi) return 'ss-status-pill active-arsa';
      return 'ss-status-pill active-taseron';
    };
    return (
      <>
        {/* Summary Cards */}
        <div className="ss-summary-grid">
          <div className="ss-summary-card">
            <div className="ss-summary-card-header">
              <div className="ss-summary-card-icon" style={{ background: '#eff6ff' }}><i className="fas fa-user" style={{ color: '#3b82f6' }} /></div>
              <div className="ss-summary-card-title">Müşteri Özeti</div>
            </div>
            {selectedCustomer ? (
              <>
                <div className="ss-summary-field">
                  <span className="ss-summary-label">Ad Soyad</span>
                  <span className="ss-summary-val" style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{selectedCustomer.adiSoyadi}</span>
                </div>
                <div className="ss-summary-field">
                  <span className="ss-summary-label">Telefon</span>
                  <span className="ss-summary-val">{selectedCustomer.telefon1 || '—'}</span>
                </div>
                {selectedCustomer.ePosta && (
                  <div className="ss-summary-field">
                    <span className="ss-summary-label">E-posta</span>
                    <span className="ss-summary-val">{selectedCustomer.ePosta}</span>
                  </div>
                )}
                {selectedCustomer.tc && (
                  <div className="ss-summary-field">
                    <span className="ss-summary-label">TC Kimlik No</span>
                    <span className="ss-summary-val">{selectedCustomer.tc}</span>
                  </div>
                )}
              </>
            ) : <em style={{ color: '#94a3b8', fontSize: 13 }}>Müşteri seçilmedi</em>}
          </div>
          <div className="ss-summary-card">
            <div className="ss-summary-card-header">
              <div className="ss-summary-card-icon" style={{ background: '#f0fdf4' }}><i className="fas fa-home" style={{ color: '#16a34a' }} /></div>
              <div className="ss-summary-card-title">Daire Özeti</div>
            </div>
            {selectedApartment ? (
              <>
                <div className="ss-summary-field">
                  <span className="ss-summary-label">Daire No</span>
                  <span className="ss-summary-val" style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Daire {selectedApartment.daireNo}</span>
                </div>
                <div className="ss-summary-field">
                  <span className="ss-summary-label">Blok / Proje</span>
                  <span className="ss-summary-val">{selectedApartment.blok?.blokAdi || '—'} · {selectedProject?.projeAdi || '—'}</span>
                </div>
                <div className="ss-summary-field">
                  <span className="ss-summary-label">Net m²</span>
                  <span className="ss-summary-val">{selectedApartment.netM2 ? `${selectedApartment.netM2} m²` : '—'}</span>
                </div>
                <div className="ss-summary-field">
                  <span className="ss-summary-label">Liste Fiyatı</span>
                  <span className="ss-summary-val" style={{ fontWeight: 700, color: '#0f172a' }}>
                    {selectedApartment.listeFiyati
                      ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(selectedApartment.listeFiyati)
                      : '—'}
                  </span>
                </div>
              </>
            ) : <em style={{ color: '#94a3b8', fontSize: 13 }}>Daire seçilmedi</em>}
          </div>
        </div>

        {/* Contract Form */}
        <div className="ss-card">
          <div className="ss-card-header">
            <div className="ss-card-title">
              <div className="ss-card-icon" style={{ background: '#fef3c7' }}><i className="fas fa-file-contract" style={{ color: '#b45309' }} /></div>
              Sözleşme Detayları
            </div>
          </div>
          <div className="ss-card-body">
            <div className="ss-form-grid" style={{ marginBottom: 20 }}>
              <div className="ss-field-wrap">
                <div className="ss-label">Sözleşme Tarihi *</div>
                <input className="ss-input" type="text" name="sozlesmeTarihi"
                  value={sozlesmeTarihiDisplay} placeholder="gg.aa.yyyy" maxLength={10}
                  onChange={e => handleDateDisplayChange('sozlesmeTarihi', e.target.value)} />
              </div>
              <div className="ss-field-wrap">
                <div className="ss-label">Sözleşme Fiyatı *</div>
                <div className="ss-input-price-wrap">
                  <input
                    className="ss-input-price"
                    type="text"
                    name="sozlesmeFiyati"
                    value={new Intl.NumberFormat('tr-TR', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(contractDetails.sozlesmeFiyati || 0)}
                    onChange={e => {
                      const value = e.target.value.replace(/[^\d.,]/g, '');
                      const numericValue = parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
                      handleContractDetailsChange({ target: { name: 'sozlesmeFiyati', value: numericValue.toString() } } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    placeholder="0"
                  />
                  <div className="ss-input-currency">₺</div>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>Örnek: 1.500.000</div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div className="ss-label" style={{ marginBottom: 10 }}>Sözleşme Durumu *</div>
              <div className="ss-status-opts">
                {([DaireDurumuEnum.Rezerve, DaireDurumuEnum.Sozlesme, DaireDurumuEnum.ArsaSahibi, DaireDurumuEnum.Taseron] as DaireDurumuEnum[]).map(d => (
                  <button key={d} className={getStatusPillClass(d)}
                    onClick={() => handleContractDetailsChange({ target: { name: 'sozlesmeDurumu', value: d } } as React.ChangeEvent<HTMLSelectElement>)}>
                    {d === DaireDurumuEnum.ArsaSahibi ? 'Arsa Sahibi' : d}
                  </button>
                ))}
              </div>
            </div>

            {contractDetails.sozlesmeDurumu === DaireDurumuEnum.Rezerve && (
              <div style={{ marginBottom: 20 }}>
                <div className="ss-label">Rezerve Bitiş Tarihi *</div>
                <input className="ss-input" type="text" name="rezerveBitisTarihi"
                  value={rezerveTarihiDisplay} placeholder="gg.aa.yyyy" maxLength={10}
                  onChange={e => handleDateDisplayChange('rezerveBitisTarihi', e.target.value)}
                  style={{ maxWidth: 260 }} />
              </div>
            )}

            <div style={{ marginBottom: 8 }}>
              <div className="ss-label" style={{ marginBottom: 8 }}>Ek Şartlar</div>
              <textarea className="ss-textarea" name="ekSartlar" value={contractDetails.ekSartlar}
                onChange={handleContractDetailsChange}
                placeholder={t('label.ara')} rows={4} />
            </div>
          </div>
        </div>

        <div className="ss-btn-row">
          <button className="ss-btn-back" onClick={goToPreviousStep}>
            <i className="fas fa-chevron-left" style={{ fontSize: 11 }} />
            Geri
          </button>
          <button className="ss-btn-save" onClick={handleSaveContract} disabled={!selectedCustomer || !selectedApartment}>
            <i className="fas fa-save" />
            Sözleşmeyi Kaydet
          </button>
        </div>
      </>
    );
  };
  
  const steps = [
    { label: 'Müşteri Seçimi' },
    { label: 'Daire Seçimi' },
    { label: 'Sözleşme Bilgileri' },
  ];

  return (
    <>
      <style>{wizardCSS}</style>
      <div className="ss-wrap">

        {/* Page Header */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '32px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 4, transition: 'color 0.15s', letterSpacing: '0.01em', width: 'fit-content' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
          >
            <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> Geri Dön
          </button>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Yeni Sözleşme</h1>
          <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>Müşteri ve proje için yeni bir sözleşme kaydı oluşturun.</p>
        </div>

        {/* Stepper */}
        <div className="ss-stepper">
          {steps.map((step, i) => {
            const stepNum = i + 1;
            const isDone = currentStep > stepNum;
            const isActive = currentStep === stepNum;
            const circleClass = isDone ? 'done' : isActive ? 'active' : 'pending';
            return (
              <React.Fragment key={stepNum}>
                <div className="ss-step">
                  <div className={`ss-step-circle ${circleClass}`}>
                    {isDone ? <i className="fas fa-check" /> : stepNum}
                  </div>
                  <div className={`ss-step-label ${circleClass}`}>{step.label}</div>
                </div>
                {i < steps.length - 1 && (
                  <div className={`ss-track ${isDone ? 'done' : 'pending'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        {currentStep === 1 && renderCustomerInfo()}
        {currentStep === 2 && renderApartmentInfo()}
        {currentStep === 3 && renderContractInfo()}

      </div>
    </>
  );
};

export default SatisSozlesmesi;